import { Goal, SubGoal, Todo } from '../services/goalService';

export type ProgressStatus = 'notStarted' | 'inProgress' | 'readyToComplete' | 'completed';

export interface SubGoalProgress {
  percent: number;
  todosTotal: number;
  todosDone: number;
  /** Explicitly completed, or every todo is done. */
  isDone: boolean;
  status: ProgressStatus;
}

export interface GoalProgress {
  percent: number;
  subGoalsTotal: number;
  subGoalsDone: number;
  todosTotal: number;
  todosDone: number;
  status: ProgressStatus;
}

const statusFor = (completed: boolean, percent: number, hasWork: boolean): ProgressStatus => {
  if (completed) return 'completed';
  if (hasWork && percent >= 100) return 'readyToComplete';
  if (percent > 0) return 'inProgress';
  return 'notStarted';
};

export const getTodoProgress = (todos: Todo[]): { total: number; done: number; percent: number } => {
  const total = todos.length;
  const done = todos.filter((todo) => todo.completed).length;
  return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
};

export const getSubGoalProgress = (subGoal: SubGoal): SubGoalProgress => {
  const todos = subGoal.todos ?? [];
  const todosTotal = todos.length;
  const todosDone = todos.filter((todo) => todo.completed).length;
  const ratio = subGoal.completed ? 1 : todosTotal > 0 ? todosDone / todosTotal : 0;
  const percent = Math.round(ratio * 100);
  return {
    percent,
    todosTotal,
    todosDone,
    isDone: subGoal.completed || (todosTotal > 0 && todosDone === todosTotal),
    status: statusFor(subGoal.completed, percent, todosTotal > 0),
  };
};

export const getGoalProgress = (goal: Goal): GoalProgress => {
  const subGoals = goal.subGoals ?? [];
  const perSubGoal = subGoals.map(getSubGoalProgress);
  const subGoalsTotal = subGoals.length;
  const subGoalsDone = goal.completed ? subGoalsTotal : perSubGoal.filter((p) => p.isDone).length;
  const todosTotal = perSubGoal.reduce((sum, p) => sum + p.todosTotal, 0);
  const todosDone = perSubGoal.reduce((sum, p) => sum + p.todosDone, 0);

  // Each subgoal weighs the same, so a one-task subgoal moves the bar as much as a ten-task one.
  const percent = goal.completed
    ? 100
    : subGoalsTotal > 0
      ? Math.round(perSubGoal.reduce((sum, p) => sum + p.percent, 0) / subGoalsTotal)
      : 0;

  return {
    percent,
    subGoalsTotal,
    subGoalsDone,
    todosTotal,
    todosDone,
    status: statusFor(goal.completed, percent, subGoalsTotal > 0),
  };
};

export const calculateGoalCompletionPercentage = (goal: Goal): number => getGoalProgress(goal).percent;

export const calculateOverallProgress = (goals: Goal[]): number => {
  return goals.length > 0
    ? Math.round(goals.reduce((sum, goal) => sum + calculateGoalCompletionPercentage(goal), 0) / goals.length)
    : 0;
};

export const countCompletedGoals = (goals: Goal[]): number => {
  return goals.filter((goal) => goal.completed).length;
};
